import Http from '@/services/http';
import { grammaticalJoin } from '@/services/arrayUtils';
import serviceExceptions from '@/domain/data/installation/engineering/loadSimulation/service-exceptions.json';
import {
  LoadSimulationStatus,
  LoadSimulationCurrentConditions,
  LoadSimulationPaperHumiditySource,
  LoadSimulationProcessingLoadSimulation,
  LoadSimulationResultHeader,
  LoadSimulationSimulationResult,
  LoadSimulationResultChart,
  LoadSimulationSimulationResultRequestOrder,
  LoadSimulationSimulationRequest,
  LoadSimulationStageSimulationResultMap,
  TemplateSimulationSimulationRequest,
  ChartQuery,
} from 'sigma-mappings';
import {
  getFormattedNumber,
  getFormattedDate,
  getFieldName,
  isNumber,
  isDate,
  getFieldNameWithOptions,
} from '@/services/exceptionHelper';
import { formats } from '@/constants/localizer';

const urls = {
  getStatus: assetModuleId => `engenharia/${assetModuleId}/simulacao-carga/status`,
  getCurrentConditions: assetModuleId => `engenharia/${assetModuleId}/simulacao-carga/condicoes-atuais`,
  getHumiditySource: assetModuleId => `engenharia/${assetModuleId}/simulacao-carga/fonte-umidade-papel`,
  postProcessPoints: assetModuleId => `engenharia/${assetModuleId}/simulacao-carga/processamento/processar-pontos`,
  getProcessingStatus: (assetModuleId, id) => `engenharia/${assetModuleId}/simulacao-carga/processamento/${id}`,
  getProcessingLog: (assetModuleId, id) => `engenharia/${assetModuleId}/simulacao-carga/processamento/${id}/log`,
  getResultStatus: (assetModuleId, resultId) => `engenharia/${assetModuleId}/simulacao-carga/resultado/${resultId}/status`,
  postPaginatedResult: (assetModuleId, resultId) => `engenharia/${assetModuleId}/simulacao-carga/resultado/${resultId}`,
  getResultHeader: (assetModuleId, resultId) => `engenharia/${assetModuleId}/simulacao-carga/resultado/${resultId}/cabecalho`,
  getResultChart: (assetModuleId, resultId) => `engenharia/${assetModuleId}/simulacao-carga/resultado/${resultId}/grafico`,
  getResultDownload: (assetModuleId, resultId) => `engenharia/${assetModuleId}/simulacao-carga/resultado/${resultId}/download`,
  postSimulation: assetModuleId => `engenharia/${assetModuleId}/simulacao-carga/simular`,
  getTemplate: () => 'engenharia/simulacao-carga/template',
  getListTemplateSimulation: () => 'engenharia/simulacao-carga/template/listagem',
  insertTemplateSimulation: (assetModuleId) => `engenharia/${assetModuleId}/simulacao-carga/template`,
  deleteTemplateSimulation: (templateId) => `engenharia/simulacao-carga/template/${templateId}`,
  updateTemplateSimulation: (templateId) => `engenharia/simulacao-carga/template/${templateId}`,
};

/* istanbul ignore next */
const getFormattedValue = (value, precision) => {
  if (isNumber(value)) {
    const number = parseFloat(value.replace(/,/g, '.'));
    return getFormattedNumber(number, precision);
  }

  if (isDate(value)) {
    return getFormattedDate(value, formats.DATETIME_SHORT_WITH_SECONDS);
  }

  return value;
};

const coolingStagesField = [
  'TipoResfriamento',
  'GradienteEnrolamentoHOleo',
  'GradienteTopoOleoAmbiente',
];

/* istanbul ignore next */
const getPointFieldAndNumber = (requestField) => {
  const field = serviceExceptions[requestField];

  if (!field) {
    const [fieldName, , rawPointText] = requestField.split(' ');
    if (!fieldName || !rawPointText) {
      return null;
    }

    const point = rawPointText.slice(0, rawPointText.length - 1);

    return {
      field: serviceExceptions[fieldName],
      point: getFieldNameWithOptions(
        'fields.engineering.load-simulation.points-value',
        {
          points: point,
        },
      ),
    };
  }

  return { field };
};

/* istanbul ignore next */
const error = {
  errorHandling: {
    responseMessageParams: {
      $MA001: (data) => {
        const returnedField = serviceExceptions[data.parameters[0]];
        return {
          NomeDoCampo: getFieldName(returnedField.label),
        };
      },
      $MA025: (data) => {
        if (!data) {
          return null;
        }
        const returnedField = serviceExceptions[data.field];
        return {
          NomeDoCampo: getFieldName(returnedField.label),
        };
      },
      $MA029: (data) => {
        const returnedField = getPointFieldAndNumber(data.parameters[2]);
        if (!returnedField.point) {
          return {
            FaixaInicial: getFormattedValue(
              data.parameters[0],
              returnedField.field.precision,
            ),
            FaixaFinal: getFormattedValue(
              data.parameters[1],
              returnedField.field.precision,
            ),
            NomeDoCampo: getFieldName(returnedField.field.label),
          };
        }

        return {
          FaixaInicial: getFormattedValue(
            data.parameters[0],
            returnedField.precision,
          ),
          FaixaFinal: getFormattedValue(
            data.parameters[1],
            returnedField.precision,
          ),
          NomeDoCampo: `${getFieldName(returnedField.label)} ${
            returnedField.point
          }`,
        };
      },
      $MA037: (data) => {
        const returnedField = serviceExceptions[data.parameters[0]];
        return {
          0: getFieldName(returnedField.label),
          1: getFormattedValue(data.parameters[1], returnedField.precision),
          2: getFormattedValue(data.parameters[2], returnedField.precision),
        };
      },
      $MA039: (data) => {
        if (!data && !data.parameters) return null;
        const returnedField = getPointFieldAndNumber(data.parameters[0]);

        if (!returnedField.point) {
          return {
            field: getFieldName(returnedField.field.label),
          };
        }

        return {
          field: `${getFieldName(returnedField.field.label)} ${
            returnedField.point
          }`,
        };
      },
      $MA052: (data) => {
        const returnedField = getPointFieldAndNumber(data.parameters[0]);
        if (!returnedField) return null;
        if (!returnedField.point) {
          return {
            0: getFieldName(returnedField.field.label),
            1: data.parameters[1],
          };
        }

        return {
          0: `${getFieldName(returnedField.field.label)} ${
            returnedField.point
          }`,
          1: data.parameters[1],
        };
      },
      $MA078: (data) => {
        const parameters = Object.entries(data.parameters);
        const commutator = parameters[0][1];
        const variablesArray = parameters
          .slice(1, parameters.length)
          .map(item => item[1]);
        const variables = grammaticalJoin(variablesArray);
        return {
          variables,
          commutator,
        };
      },
      $MA085: (data) => {
        if (!data && !data.parameters) return null;
        const returnedField = serviceExceptions[data.parameters[0]];
        return {
          variable: getFieldName(returnedField.label),
        };
      },
      $MA100: (data) => ({
        0: data.field.split('.').pop(),
        1: data.parameters[0],
      }),
      $MA103: (data) => {
        if (!data && !data.parameters) return null;
        const returnedField = serviceExceptions[data.parameters[0]];

        if (coolingStagesField.some(x => x === data.parameters[0])) {
          const coolingStageOptions = { number: data.field.charAt(21) };
          const coolingStageLabel = 'asset-information.cooling-stages.stage-number';
          const coolingStage = getFieldNameWithOptions(
            coolingStageLabel,
            coolingStageOptions,
          );

          return {
            0: `${getFieldName(returnedField.label)} (${coolingStage})`,
          };
        }

        return { 0: getFieldName(returnedField.label) };
      },
    },
  },
};

export default {
  getStatus(assetModuleId) {
    const http = new Http(error);
    return http
      .get(urls.getStatus(assetModuleId))
      .then(({ data }) => new LoadSimulationStatus(data));
  },
  getCurrentConditions(assetModuleId) {
    const http = new Http(error);
    return http
      .get(urls.getCurrentConditions(assetModuleId))
      .then(({ data }) => new LoadSimulationCurrentConditions(data));
  },
  getHumiditySource(assetModuleId) {
    const http = new Http(error);
    return http
      .get(urls.getHumiditySource(assetModuleId))
      .then(({ data }) => data.map(item => new LoadSimulationPaperHumiditySource(item)));
  },
  processPoints(assetModuleId, file) {
    const http = new Http(error);
    const requestOptions = {
      headers: { post: { 'Content-Type': 'multipart/form-data' } },
    };
    const payload = new FormData();
    payload.append('arquivo', file, file.name);

    return http
      .post(urls.postProcessPoints(assetModuleId), payload, requestOptions)
      .then(({ data }) => data);
  },
  getProcessingStatus(assetModuleId, id) {
    const http = new Http(error);
    return http
      .get(urls.getProcessingStatus(assetModuleId, id))
      .then(({ data }) => new LoadSimulationProcessingLoadSimulation(data));
  },
  getResultStatus(assetModuleId, resultId) {
    const http = new Http(error);
    return http
      .get(urls.getResultStatus(assetModuleId, resultId))
      .then(({ data }) => LoadSimulationStageSimulationResultMap[data]);
  },
  getPaginatedResult(assetModuleId, resultId, params) {
    const http = new Http(error);
    const payload = new LoadSimulationSimulationResultRequestOrder(params);
    return http
      .post(urls.postPaginatedResult(assetModuleId, resultId), payload)
      .then(({ data }) => new LoadSimulationSimulationResult(data));
  },
  getResultHeader(assetModuleId, resultId) {
    const http = new Http(error);
    return http
      .get(urls.getResultHeader(assetModuleId, resultId))
      .then(({ data }) => new LoadSimulationResultHeader(data));
  },
  getResultChart(assetModuleId, resultId, chartFilters) {
    const chartQuery = new ChartQuery(chartFilters);
    const http = new Http(error);
    return http
      .get(urls.getResultChart(assetModuleId, resultId), { params: chartQuery })
      .then(({ data }) => data.map(item => new LoadSimulationResultChart(item)));
  },
  simulation(assetModuleId, isVisualization, params) {
    const http = new Http(error);
    const payload = isVisualization ? params : new LoadSimulationSimulationRequest(params);

    return http
      .post(urls.postSimulation(assetModuleId), payload)
      .then(({ data }) => data);
  },
  downloadLog(assetModuleId, id) {
    const http = new Http({ download: true });
    return http.get(urls.getProcessingLog(assetModuleId, id));
  },
  downloadResult(assetModuleId, resultId) {
    const http = new Http({ download: true });
    return http.get(urls.getResultDownload(assetModuleId, resultId));
  },
  downloadTemplate() {
    const http = new Http({ download: true });
    return http.get(urls.getTemplate());
  },
  getListTemplateSimulation(companyId, page, perPage) {
    const http = new Http(error);
    const payload = {
      empresaId: companyId,
      pagina: page || 1,
      tamanhoPagina: perPage || 10,
    };

    return http
      .post(urls.getListTemplateSimulation(), payload)
      .then(({ data }) => data);
  },
  insertTemplateSimulation(assetModuleId, params) {
    const http = new Http(error);
    const payload = new TemplateSimulationSimulationRequest(params);
    return http.post(urls.insertTemplateSimulation(assetModuleId), payload)
      .then(({ data }) => data);
  },
  deleteTemplateSimulation(templateId) {
    const http = new Http(error);
    return http.delete(urls.deleteTemplateSimulation(templateId));
  },
  updateTemplateSimulation(templateId, params) {
    const http = new Http(error);
    const payload = new TemplateSimulationSimulationRequest(params);
    return http.put(urls.updateTemplateSimulation(templateId), payload);
  },
};
